import { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  Trash2,
  Download,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Pencil,
  Save,
  X,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatDate, cn } from '@/lib/utils';

const documentTypes = [
  { value: 'w2', label: 'W-2' },
  { value: '1099_nec', label: '1099-NEC' },
  { value: '1099_misc', label: '1099-MISC' },
  { value: '1099_int', label: '1099-INT' },
  { value: '1099_div', label: '1099-DIV' },
  { value: '1099_b', label: '1099-B' },
  { value: '1099_k', label: '1099-K' },
  { value: '1099_r', label: '1099-R' },
  { value: '1098', label: '1098 (Mortgage)' },
  { value: '1098_e', label: '1098-E (Student Loan)' },
  { value: '1098_t', label: '1098-T (Tuition)' },
  { value: 'k1', label: 'Schedule K-1' },
  { value: 'crypto_statement', label: 'Crypto Statement' },
  { value: 'previous_return', label: 'Previous Tax Return' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'other', label: 'Other' },
];

export function DocumentsPage() {
  const { selectedYear, documents, fetchDocuments, isLoading } = useTaxStore();
  const [uploadType, setUploadType] = useState('w2');
  const [uploading, setUploading] = useState(false);
  const [employer, setEmployer] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [viewingExtracted, setViewingExtracted] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [loadingExtracted, setLoadingExtracted] = useState(false);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  const handleStartEdit = () => {
    setEditData({
      employer: extractedData?.employer || '',
      ein: extractedData?.ein || '',
      wages: extractedData?.wages || '',
      federalWithheld: extractedData?.federalWithheld || '',
      stateWithheld: extractedData?.stateWithheld || '',
      state: extractedData?.state || '',
      socialSecurityWages: extractedData?.socialSecurityWages || '',
      medicareWages: extractedData?.medicareWages || '',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedDoc) return;
    setSaving(true);
    try {
      // Convert string values to numbers where needed
      const updates: Record<string, any> = {
        employer: editData.employer || undefined,
        ein: editData.ein || undefined,
        state: editData.state || undefined,
      };

      if (editData.wages) updates.wages = parseFloat(editData.wages) || 0;
      if (editData.federalWithheld) updates.federalWithheld = parseFloat(editData.federalWithheld) || 0;
      if (editData.stateWithheld) updates.stateWithheld = parseFloat(editData.stateWithheld) || 0;
      if (editData.socialSecurityWages) updates.socialSecurityWages = parseFloat(editData.socialSecurityWages) || 0;
      if (editData.medicareWages) updates.medicareWages = parseFloat(editData.medicareWages) || 0;

      const result = await api.updateDocument(selectedDoc.id, { extractedData: updates });
      setExtractedData(result.extractedData);
      setIsEditing(false);
      fetchDocuments(selectedYear);
    } catch (err) {
      console.error('Error saving:', err);
    }
    setSaving(false);
  };

  const handleReprocess = async (doc: any) => {
    setReprocessingId(doc.id);
    try {
      let result = await api.processDocument(doc.id, selectedYear);

      // If still processing (async job), poll until complete
      const maxPollAttempts = 30;
      let pollCount = 0;
      while (result.status === 'processing' && pollCount < maxPollAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        result = await api.processDocument(doc.id, selectedYear);
        pollCount++;
      }

      fetchDocuments(selectedYear);
    } catch (err) {
      console.error('Reprocess error:', err);
    }
    setReprocessingId(null);
  };

  const handleViewExtracted = async (doc: any) => {
    setSelectedDoc(doc);
    setLoadingExtracted(true);
    setViewingExtracted(true);
    try {
      const data = await api.getDocument(doc.id);
      setExtractedData(data.extractedData);
    } catch (err) {
      console.error('Error fetching extracted data:', err);
    }
    setLoadingExtracted(false);
  };

  useEffect(() => {
    fetchDocuments(selectedYear);
  }, [selectedYear, fetchDocuments]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setUploading(true);
      for (const file of acceptedFiles) {
        try {
          // Get upload URL
          const { uploadUrl, documentId } = await api.getUploadUrl({
            fileName: file.name,
            fileType: file.type,
            documentType: uploadType,
            year: selectedYear,
            employer: employer || undefined,
          });

          // Upload file to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          // Process with Textract (may be async for multi-page PDFs)
          let result = await api.processDocument(documentId, selectedYear);

          // If still processing (async job), poll until complete
          const maxPollAttempts = 30; // Max 60 seconds of polling
          let pollCount = 0;
          while (result.status === 'processing' && pollCount < maxPollAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            result = await api.processDocument(documentId, selectedYear);
            pollCount++;
            fetchDocuments(selectedYear); // Update list to show processing status
          }

          // Refresh list
          fetchDocuments(selectedYear);
        } catch (err) {
          console.error('Upload error:', err);
        }
      }
      setUploading(false);
      setEmployer('');
    },
    [uploadType, selectedYear, employer, fetchDocuments]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    multiple: true,
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.deleteDocument(id);
      fetchDocuments(selectedYear);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Upload Documents</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Document Type</label>
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {uploadType === 'w2' && (
            <div>
              <label className="block text-sm font-medium mb-2">Employer Name</label>
              <input
                type="text"
                value={employer}
                onChange={(e) => setEmployer(e.target.value)}
                placeholder="e.g., Acme Corp"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}
        </div>

        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-smooth',
            isDragActive
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
          )}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-primary animate-spin mb-3" />
              <p className="text-muted-foreground">Uploading and processing...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="font-medium mb-1">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-muted-foreground">
                or click to select files (PDF, PNG, JPG)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">
          Documents ({documents.length})
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No documents uploaded yet</p>
            <p className="text-sm">Upload your W-2s, 1099s, and other tax documents</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    {getStatusIcon(doc.status)}
                  </div>
                  <div>
                    <p className="font-medium">{doc.fileName}</p>
                    <p className="text-sm text-muted-foreground">
                      {documentTypes.find((t) => t.value === doc.type)?.label || doc.type}
                      {doc.employer && ` - ${doc.employer}`}
                      {' '}&bull;{' '}
                      {formatDate(doc.uploadedAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.status === 'processed' && (
                    <button
                      onClick={() => handleViewExtracted(doc)}
                      className="p-2 rounded-lg hover:bg-accent transition-smooth"
                      title="View extracted data"
                    >
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                  <button
                    onClick={() => handleReprocess(doc)}
                    disabled={reprocessingId === doc.id}
                    className="p-2 rounded-lg hover:bg-accent transition-smooth disabled:opacity-50"
                    title="Reprocess document"
                  >
                    <RefreshCw className={cn("h-4 w-4 text-muted-foreground", reprocessingId === doc.id && "animate-spin")} />
                  </button>
                  <button
                    onClick={() => {/* Download */}}
                    className="p-2 rounded-lg hover:bg-accent transition-smooth"
                    title="Download"
                  >
                    <Download className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 rounded-lg hover:bg-destructive/10 transition-smooth"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extracted Data Modal */}
      {viewingExtracted && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold">
                Extracted Data - {selectedDoc?.fileName}
              </h3>
              <div className="flex items-center gap-2">
                {selectedDoc?.type === 'w2' && !isEditing && (
                  <button
                    onClick={handleStartEdit}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                )}
                {isEditing && (
                  <>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      Save
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted/80"
                    >
                      <X className="h-3 w-3" />
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setViewingExtracted(false);
                    setExtractedData(null);
                    setSelectedDoc(null);
                    setIsEditing(false);
                  }}
                  className="p-1 rounded hover:bg-muted"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loadingExtracted ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              ) : extractedData ? (
                <div className="space-y-4">
                  {/* Form Fields */}
                  {extractedData.formFields && Object.keys(extractedData.formFields).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Form Fields</h4>
                      <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                        {Object.entries(extractedData.formFields).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{key}</span>
                            <span className="font-medium">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Values (W-2 specific) */}
                  {(extractedData.wages || extractedData.federalWithheld || selectedDoc?.type === 'w2') && (
                    <div>
                      <h4 className="font-medium mb-2">W-2 Values</h4>
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">Employer Name</label>
                              <input
                                type="text"
                                value={editData.employer}
                                onChange={(e) => setEditData({ ...editData, employer: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="Employer name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">EIN</label>
                              <input
                                type="text"
                                value={editData.ein}
                                onChange={(e) => setEditData({ ...editData, ein: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="XX-XXXXXXX"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">Wages (Box 1)</label>
                              <input
                                type="number"
                                value={editData.wages}
                                onChange={(e) => setEditData({ ...editData, wages: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="0.00"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">Federal Withheld (Box 2)</label>
                              <input
                                type="number"
                                value={editData.federalWithheld}
                                onChange={(e) => setEditData({ ...editData, federalWithheld: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">State</label>
                              <input
                                type="text"
                                value={editData.state}
                                onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="FL"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-muted-foreground mb-1">State Withheld</label>
                              <input
                                type="number"
                                value={editData.stateWithheld}
                                onChange={(e) => setEditData({ ...editData, stateWithheld: e.target.value })}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {(extractedData.employer || extractedData.ein) && (
                            <div className="col-span-2 bg-muted/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Employer</p>
                              <p className="font-semibold">{extractedData.employer || 'Unknown'}</p>
                              {extractedData.ein && (
                                <p className="text-sm text-muted-foreground mt-1">EIN: {extractedData.ein}</p>
                              )}
                            </div>
                          )}
                          {extractedData.wages && (
                            <div className="bg-green-500/10 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Wages</p>
                              <p className="text-lg font-semibold text-green-500">
                                ${typeof extractedData.wages === 'number' ? extractedData.wages.toLocaleString() : extractedData.wages}
                              </p>
                            </div>
                          )}
                          {extractedData.federalWithheld && (
                            <div className="bg-blue-500/10 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Federal Withheld</p>
                              <p className="text-lg font-semibold text-blue-500">
                                ${typeof extractedData.federalWithheld === 'number' ? extractedData.federalWithheld.toLocaleString() : extractedData.federalWithheld}
                              </p>
                            </div>
                          )}
                          {extractedData.socialSecurityWages && (
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">SS Wages</p>
                              <p className="font-semibold">
                                ${typeof extractedData.socialSecurityWages === 'number' ? extractedData.socialSecurityWages.toLocaleString() : extractedData.socialSecurityWages}
                              </p>
                            </div>
                          )}
                          {extractedData.medicareWages && (
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Medicare Wages</p>
                              <p className="font-semibold">
                                ${typeof extractedData.medicareWages === 'number' ? extractedData.medicareWages.toLocaleString() : extractedData.medicareWages}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Key Values (1098 Mortgage specific) */}
                  {(extractedData.mortgageInterest || extractedData.realEstateTaxes || extractedData.mortgagePrincipal) && (
                    <div>
                      <h4 className="font-medium mb-2">1098 Mortgage Values</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {extractedData.mortgageInterest && (
                          <div className="bg-emerald-500/10 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">Mortgage Interest (Box 1)</p>
                            <p className="text-lg font-semibold text-emerald-500">
                              ${typeof extractedData.mortgageInterest === 'number' ? extractedData.mortgageInterest.toLocaleString() : extractedData.mortgageInterest}
                            </p>
                          </div>
                        )}
                        {extractedData.realEstateTaxes && (
                          <div className="bg-blue-500/10 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">Property Taxes (Box 10)</p>
                            <p className="text-lg font-semibold text-blue-500">
                              ${typeof extractedData.realEstateTaxes === 'number' ? extractedData.realEstateTaxes.toLocaleString() : extractedData.realEstateTaxes}
                            </p>
                          </div>
                        )}
                        {extractedData.mortgagePrincipal && (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">Outstanding Principal (Box 2)</p>
                            <p className="font-semibold">
                              ${typeof extractedData.mortgagePrincipal === 'number' ? extractedData.mortgagePrincipal.toLocaleString() : extractedData.mortgagePrincipal}
                            </p>
                          </div>
                        )}
                        {extractedData.pointsPaid && (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">Points Paid (Box 6)</p>
                            <p className="font-semibold">
                              ${typeof extractedData.pointsPaid === 'number' ? extractedData.pointsPaid.toLocaleString() : extractedData.pointsPaid}
                            </p>
                          </div>
                        )}
                        {extractedData.mortgageInsurance && (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground">Mortgage Insurance (Box 5)</p>
                            <p className="font-semibold">
                              ${typeof extractedData.mortgageInsurance === 'number' ? extractedData.mortgageInsurance.toLocaleString() : extractedData.mortgageInsurance}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Values (1098-E Student Loan specific) */}
                  {extractedData.studentLoanInterest && (
                    <div>
                      <h4 className="font-medium mb-2">1098-E Student Loan Values</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-purple-500/10 rounded-lg p-3">
                          <p className="text-sm text-muted-foreground">Student Loan Interest (Box 1)</p>
                          <p className="text-lg font-semibold text-purple-500">
                            ${typeof extractedData.studentLoanInterest === 'number' ? extractedData.studentLoanInterest.toLocaleString() : extractedData.studentLoanInterest}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Crypto Holdings */}
                  {extractedData.cryptoHoldings && extractedData.cryptoHoldings.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Crypto Holdings</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-border rounded">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="border border-border px-3 py-2 text-left">Asset</th>
                              <th className="border border-border px-3 py-2 text-right">Quantity</th>
                              <th className="border border-border px-3 py-2 text-right">Price</th>
                              <th className="border border-border px-3 py-2 text-right">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {extractedData.cryptoHoldings.map((holding: any, idx: number) => (
                              <tr key={idx}>
                                <td className="border border-border px-3 py-2 font-medium">{holding.asset}</td>
                                <td className="border border-border px-3 py-2 text-right">{holding.quantity.toLocaleString()}</td>
                                <td className="border border-border px-3 py-2 text-right">${holding.price.toLocaleString()}</td>
                                <td className="border border-border px-3 py-2 text-right font-medium text-green-500">${holding.value.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Tables */}
                  {extractedData.tables && extractedData.tables.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Tables</h4>
                      {extractedData.tables.map((table: any[][], idx: number) => (
                        <div key={idx} className="overflow-x-auto mb-3">
                          <table className="w-full text-sm border border-border rounded">
                            <tbody>
                              {table.map((row, rowIdx) => (
                                <tr key={rowIdx} className={rowIdx === 0 ? 'bg-muted/50' : ''}>
                                  {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className="border border-border px-2 py-1">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No data message */}
                  {(!extractedData.formFields || Object.keys(extractedData.formFields).length === 0) &&
                   !extractedData.wages &&
                   (!extractedData.tables || extractedData.tables.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">
                      No structured data could be extracted from this document.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No extracted data available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
